import { useEffect, useState } from 'react';

import { ErroDeRede, carregarCatalogo } from '../nucleo/api.js';
import {
  diasJaNoRotulo,
  eixoCurto,
  ganhoAparente,
  maiorPerdaEmOutroEixo,
  serieDeReferencia,
} from '../nucleo/estocagem.js';
import { NOME_DA_ESPECIE } from '../nucleo/especies.js';
import { enxuto, expressivo, numero, plural, porcento } from '../nucleo/numero.js';
import { SeloDaProcedencia } from '../componentes/SeloDeConfianca.jsx';
import { paraRota } from '../nucleo/rota.js';

/**
 * Uma categoria aberta até o fim — a tela que o consultor cita.
 *
 * O que distingue esta tela da lista não é mais detalhe: é a **fonte inteira**
 * e as **séries de estocagem**. A citação completa está aqui em texto
 * selecionável, com autor, ano, revista, DOI, método e tamanho de amostra,
 * porque é isso que se cola num relatório de viabilidade — e porque uma fonte
 * resumida a "Embrapa, 2016" não é verificável por quem recebe o estudo.
 *
 * O campo `derivado` ganha linha própria. Quando ele está presente, um dos
 * números desta ficha **não foi medido**: foi calculado a partir de outro. No
 * chorume de suíno, por exemplo, o ST é conta reversa do SV. Publicar ST e SV
 * lado a lado sem dizer qual dos dois saiu do frasco é o tipo de omissão que
 * ninguém percebe e que inverte uma conclusão.
 *
 * **As séries de estocagem são o coração desta tela.** Dejeto guardado perde
 * potencial de metano — muito, cedo, e sem volta. Cada série é um par
 * medido (de → para) com o eixo declarado, porque o eixo é onde mora a
 * armadilha: existe uma série em que guardar o dejeto 90 dias a 5 °C parece
 * **ganhar** 9,6% de metano. Não ganhou. O SV da massa úmida caiu, e dividir
 * por um denominador menor levanta o quociente; no eixo por tonelada o mesmo
 * frasco perde 11,6%. Essa série aparece aqui marcada como enganosa, com a
 * explicação ao lado, em vez de ser omitida: quem só esconde o dado não impede
 * que o produtor encontre o mesmo gráfico num folheto.
 */

function Ficha({ item, rotuloDoAnalogo }) {
  const linhas = [];
  const emprestado = Boolean(item.analogo) && item.bmp != null;

  if (item.bmp != null) {
    linhas.push([
      'Potencial de metano (BMP)',
      `${expressivo(item.bmp)} NmL CH₄/g SV`,
      item.bmp_faixa
        ? `faixa publicada de ${enxuto(item.bmp_faixa[0], 2)} a ${enxuto(item.bmp_faixa[1], 2)}`
        : 'sem faixa publicada',
    ]);
  }
  if (item.bmp_n != null) {
    linhas.push([
      'Ensaios independentes',
      plural(item.bmp_n, 'ensaio', 'ensaios'),
      emprestado ? `em ${rotuloDoAnalogo || item.analogo}, não nesta categoria` : '',
    ]);
  }
  if (item.ch4_pct != null) {
    linhas.push(['Metano no biogás', `${numero(item.ch4_pct, 1)} %`, '']);
  }
  if (item.st_pct != null) {
    linhas.push(['Sólidos totais (ST)', `${numero(item.st_pct, 2)} %`, 'da massa úmida']);
  }
  if (item.sv_pct_do_st != null) {
    linhas.push(['Sólidos voláteis (SV)', `${numero(item.sv_pct_do_st, 2)} %`, 'do ST']);
  }
  if (item.sv_pct_umido != null) {
    linhas.push(['SV na massa úmida', `${numero(item.sv_pct_umido, 3)} %`, '']);
  }
  if (item.rendimento_nm3_t != null) {
    linhas.push([
      'Rendimento por tonelada',
      `${enxuto(item.rendimento_nm3_t, 2)} Nm³ CH₄/t`,
      item.rendimento_faixa_nm3_t
        ? `de ${enxuto(item.rendimento_faixa_nm3_t[0], 2)} a ${enxuto(item.rendimento_faixa_nm3_t[1], 2)}`
        : item.rendimento_faixa_ausente_porque || '',
    ]);
  }
  if (item.dejeto_kg_cab_dia != null) {
    linhas.push([
      'Dejeto por cabeça, por dia',
      `${enxuto(item.dejeto_kg_cab_dia, 1)} kg`,
      '',
    ]);
  }
  if (item.nm3_ch4_por_cabeca_dia != null) {
    linhas.push([
      'Metano por cabeça, por dia',
      `${enxuto(item.nm3_ch4_por_cabeca_dia, 4)} Nm³ CH₄`,
      '',
    ]);
  }
  linhas.push(['Forma do material', item.forma, '']);
  // Separado da forma em 15/10/2026. Estavam na mesma linha, e ler "Forma do
  // material: chorume — não fibroso" sugere que fibroso é textura. Não é:
  // `fibroso` no motor é composição lignocelulósica, e o que ela muda é o
  // teto físico de Buswell. Foi essa confusão que marcou dejeto seco de
  // poedeira sem cama como fibroso.
  linhas.push([
    'Composição',
    item.fibroso ? 'lignocelulósica' : 'não lignocelulósica',
    item.fibroso
      ? 'teto físico de 414 em vez de 496 NmL CH₄/g SV, e mais tempo de retenção'
      : '',
  ]);

  return (
    <dl className="ficha">
      {linhas.map(([rotulo, valor, nota]) => (
        <div className="ficha__linha" key={rotulo}>
          <dt>{rotulo}</dt>
          <dd>
            {valor}
            {nota ? <span className="terciario"> — {nota}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Qual número desta ficha é conta e não medição. */
function Derivado({ item }) {
  if (!item.derivado) return null;

  const NOME = {
    st_pct: 'o teor de sólidos totais (ST)',
    sv_pct_umido: 'o teor de sólidos voláteis na massa úmida',
    sv_pct_do_st: 'a razão SV/ST',
  };

  return (
    <p className="ressalva">
      Nesta ficha, {NOME[item.derivado] ?? item.derivado} é <strong>derivado</strong>, não medido:
      saiu de conta a partir dos outros dois. A fonte da razão usada está abaixo.
    </p>
  );
}

function Serie({ serie, eReferencia = false }) {
  const perda = serie.perda_pct;
  // A regra do sinal vem do núcleo, não daqui: a tela da evidência inteira
  // decide "é ganho?" com a mesma função, e duas telas que divergissem nisso
  // chamariam a mesma medição de ganho numa e de perda na outra.
  const ganho = ganhoAparente(serie);

  return (
    <article
      className={`serie ${serie.eixo_engana ? 'serie--engana' : ''} ${
        eReferencia ? 'serie--referencia' : ''
      }`}
    >
      <h4 className="serie__titulo">
        {serie.de} → {serie.para}
        {serie.dias != null ? <span className="terciario"> ({serie.dias} dias)</span> : null}
        {eReferencia ? <span className="serie__marca">é esta a referência do app</span> : null}
      </h4>

      <p className="serie__numeros">
        <span>
          {enxuto(serie.valor_de, 2)} → {enxuto(serie.valor_para, 2)}{' '}
          <span className="terciario">{serie.unidade}</span>
        </span>
        <strong className={ganho ? 'serie__ganho' : 'serie__perda'}>
          {ganho
            ? `aparente ganho de ${porcento(Math.abs(perda), 1)}`
            : `perda de ${porcento(perda, 1)}`}
        </strong>
      </p>

      <p className="terciario serie__eixo">
        Eixo: {eixoCurto(serie.eixo)}
        {serie.perda_e_piso ? ' · este é o piso, a perda real pode ser maior' : ''}
      </p>

      {serie.eixo_engana ? (
        <p className="ressalva ressalva--grave">
          <strong>Este eixo engana.</strong> {serie.observacao}
        </p>
      ) : serie.observacao ? (
        <p className="terciario">{serie.observacao}</p>
      ) : null}

      <p className="serie__fonte">{serie.fonte}</p>
    </article>
  );
}

function Estocagem({ item }) {
  const series = item.estocagem ?? [];

  if (!series.length) {
    return (
      <section className="bloco">
        <h3 className="rotulo-secao">Perda por estocagem</h3>
        <p className="secundario">
          Nenhuma série medida para este material. A perda existe — é física, não é opcional — mas
          dizer de quanto sem medição seria inventar.
        </p>
      </section>
    );
  }

  const porTonelada = series.filter((s) => s.eixo === 'por tonelada de material');
  const enganosas = series.filter((s) => s.eixo_engana).length;

  // A API aponta a série de onde o número saiu, em vez de copiar os campos
  // dela. Sem esse ponteiro a frase "a referência é 85%" ficava solta no meio
  // de cinco séries que vão de -9,6% a 85%, e o leitor tinha de adivinhar qual
  // delas é. Pior: em `bovino_corte_confinado` o maior número da página é 94%
  // e a referência é 85%, o que, sem explicação, faz parecer que o app
  // escolheu o número menor para lisonjear a conta. A razão é o eixo, e ela
  // precisa estar escrita.
  const iReferencia = item.perda_se_estocar_indice;
  const referencia = serieDeReferencia(item);
  const maior = maiorPerdaEmOutroEixo(item);

  return (
    <section className="bloco">
      <h3 className="rotulo-secao">Perda por estocagem</h3>
      <p className="secundario">
        {plural(series.length, 'série medida', 'séries medidas')} para este material.
        {item.perda_se_estocar_pct != null ? (
          <>
            {' '}
            A perda que o app usa como referência é de{' '}
            <strong>{porcento(item.perda_se_estocar_pct, 0)}</strong>
            {referencia ? (
              <>
                , medida {eixoCurto(referencia.eixo)}, de “{referencia.de}” para
                “{referencia.para}”
                {referencia.dias != null && !diasJaNoRotulo(referencia)
                  ? ` em ${referencia.dias} dias`
                  : ''}
              </>
            ) : null}
            {/* A ressalva de piso vem DEPOIS da série, não no meio dela. Antes
                ficava logo após o número, e a frase saía "é de 85% — e esse
                número é piso, não teto, medida por tonelada, de ... para ...":
                o aparte cortava a locução pelo meio e "não teto, medida por
                tonelada" lia como se o teto fosse a medida. A ordem certa é
                número → condição medida → ressalva. */}
            {item.perda_se_estocar_e_piso ? ' — e esse número é piso, não teto' : ''}.
          </>
        ) : null}
        {maior ? (
          <>
            {' '}
            O maior número desta ficha não é esse: é a perda de{' '}
            <strong>{porcento(maior.perda_pct, 0)}</strong>, e ela está no eixo{' '}
            {eixoCurto(maior.eixo)}, que mede concentração e não massa. A referência fica no eixo
            por tonelada porque é o que o produtor bombeia — não porque seja o número menor.
          </>
        ) : null}
        {porTonelada.length ? (
          <>
            {' '}
            {plural(porTonelada.length, 'série está', 'séries estão')} no eixo por tonelada, que é o
            que o produtor sente: ele bombeia tonelada, não grama de sólido volátil.
          </>
        ) : null}
        {enganosas ? (
          <>
            {' '}
            <strong>
              {plural(enganosas, 'série tem o eixo marcado como enganoso', 'séries têm o eixo marcado como enganoso')}
            </strong>{' '}
            e está aqui com a explicação, em vez de omitida.
          </>
        ) : null}
      </p>
      <div className="empilha">
        {series.map((s, i) => (
          <Serie key={`${s.de}|${s.para}|${s.eixo}|${i}`} serie={s} eReferencia={i === iReferencia} />
        ))}
      </div>
    </section>
  );
}

function NaoAchei({ chave }) {
  return (
    <section className="cartao" role="alert">
      <h2 className="recusa__titulo">Não tenho essa categoria</h2>
      <p>
        O catálogo não tem <code>{chave}</code>. Pode ser um link antigo: a lista completa está na
        tela de materiais.
      </p>
      <p>
        <a href={paraRota('substratos')}>← Ver todas as categorias</a>
      </p>
    </section>
  );
}

function SemCatalogo({ erro }) {
  const offline = erro instanceof ErroDeRede && erro.offline;
  return (
    <section className="cartao" role="alert">
      <h2 className="recusa__titulo">
        {offline ? 'Sem conexão e sem cópia guardada' : 'Não consegui carregar o catálogo'}
      </h2>
      <p>{offline ? 'Abra o app uma vez com internet e esta ficha passa a abrir sem sinal.' : erro?.message}</p>
    </section>
  );
}

export function Substrato({ chave }) {
  const [catalogo, setCatalogo] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let vivo = true;
    carregarCatalogo()
      .then((c) => {
        if (vivo) setCatalogo(c);
      })
      .catch((e) => {
        if (vivo) setErro(e);
      });
    return () => {
      vivo = false;
    };
  }, []);

  if (erro) return <SemCatalogo erro={erro} />;
  if (!catalogo) {
    return (
      <p className="secundario" role="status">
        Carregando a ficha…
      </p>
    );
  }

  const item = (catalogo.itens ?? []).find((i) => i.chave === chave);
  if (!item) return <NaoAchei chave={chave} />;

  const rotuloDoAnalogo = item.analogo
    ? ((catalogo.itens ?? []).find((i) => i.chave === item.analogo)?.rotulo ?? item.analogo)
    : '';
  // "Emprestado" não é "tem análogo": das cinco entradas com análogo, quatro
  // não publicam número nenhum, e para elas o bloco "Material parecido" já é
  // honesto sozinho. O problema é publicar o número do vizinho debaixo de um
  // título que diz "O que foi medido".
  const emprestado = Boolean(item.analogo) && item.bmp != null;

  return (
    <div className="empilha-largo">
      <p className="substrato__volta">
        <a href={paraRota('substratos')}>← Todas as categorias</a>
      </p>

      <section className="bloco">
        <header className="substrato__cabeca">
          <h2 className="simulador__titulo">{item.rotulo}</h2>
          <SeloDaProcedencia valor={item.confianca} detalhado />
        </header>
        <p className="secundario">
          {NOME_DA_ESPECIE[item.especie] ?? item.especie} · {item.forma}
        </p>
      </section>

      <section className="bloco">
        <h3 className="rotulo-secao">
          {emprestado ? 'O que foi medido — em outro material' : 'O que foi medido'}
        </h3>
        {emprestado ? (
          <p className="terciario">
            Nenhum número desta ficha foi medido nesta categoria: todos vêm de{' '}
            <a href={paraRota('substratos', item.analogo)}>{rotuloDoAnalogo}</a>, aplicados aqui
            por analogia porque não existe ensaio separado desta fase. A própria fonte declara a
            herança, e o simulador recusa a conta com esta categoria.
          </p>
        ) : null}
        <Ficha item={item} rotuloDoAnalogo={rotuloDoAnalogo} />
        <Derivado item={item} />
      </section>

      {item.pendencia ? (
        <section className="bloco">
          <h3 className="rotulo-secao">O que falta</h3>
          <div className="pendencia">
            <p>{item.pendencia}</p>
          </div>
        </section>
      ) : null}

      {/* Sai de cena quando o número é emprestado: ali o bloco dos números já
          nomeia o análogo, com precisão, e a frase daqui — "o análogo está
          aqui como análogo e não como valor emprestado" — afirmaria
          exatamente o contrário do que a ficha acabou de imprimir. Para as
          quatro entradas que têm análogo e não publicam número, a frase é
          verdadeira e o bloco fica. */}
      {item.analogo && !emprestado ? (
        <section className="bloco">
          <h3 className="rotulo-secao">Material parecido</h3>
          <p className="secundario">
            O mais próximo no catálogo é{' '}
            <a href={paraRota('substratos', item.analogo)}>{rotuloDoAnalogo}</a>
            . Serve para conversar sobre ordem de grandeza. Não serve de linha de estudo, e a conta
            com esta categoria continua recusada — é por isso que o análogo está aqui como análogo e
            não como valor emprestado.
          </p>
        </section>
      ) : null}

      <Estocagem item={item} />

      <section className="bloco">
        <h3 className="rotulo-secao">De onde vem</h3>
        {item.fonte ? <p className="fonte-inteira">{item.fonte}</p> : null}
        {item.fonte_da_razao_sv_st ? (
          <>
            <h4 className="rotulo-secao">Fonte da razão SV/ST</h4>
            <p className="fonte-inteira">{item.fonte_da_razao_sv_st}</p>
          </>
        ) : null}
        {item.observacao ? (
          <>
            <h4 className="rotulo-secao">Observação</h4>
            <p className="fonte-inteira">{item.observacao}</p>
          </>
        ) : null}
      </section>
    </div>
  );
}
